import { XY } from 'utils/misc';

/**
 * Wrapper for HTMLElement which is being watched by MouseTracker.
 * Contains data about mouse interaction with the HTMLElement.
 * Has grab interface (grab, grabHistory).
 * @class WatchedElement
 */
class WatchedElement {
  /**
   * Creates an instance of WatchedElement.
   * @param {HTMLElement} el
   */
  constructor(el) {
    this.el = el;

    /** Current cursor position relative to el box */
    this.curr = new XY();
    /** Previous cursor position relative to el box */
    this.prev = new XY();

    this.hover = false;

    this.grab = false;
    /**
     * Can be accessed after grab has ended.
     * Resets when grab starts
     * @type Array.<XY>
     * */
    this.grabHistory = [];
  }

  /**
   * Updates relative positions and appends to grab history
   * @protected
   * @param {XY} pos - current global cursor position
   */
  _updatePosition(pos) {
    const box = this.el.getBoundingClientRect();

    this.prev.x = this.curr.x;
    this.prev.y = this.curr.y;

    this.curr.x = pos.x - box.x;
    this.curr.y = pos.y - box.y;

    if (this.grab) {
      this.grabHistory.push(pos.copy());
    }
  }

  /**
   * Called by MouseTracker when grab starts
   * @protected
   */
  _grabStart() {
    this.grab = true;
    this.grabHistory = [];
  }

  /**
   * Called by MouseTracker when grab ends
   * @protected
   */
  _grabEnd() {
    this.grab = false;
  }
}

/**
 * Class used for tracking the position of the cursor, mouse buttons.
 * Can also watch specific HTMLElement-s via WatchedElement
 * @class MouseTracker
 */
class MouseTracker {
  constructor() {
    /** Current cursor position  */
    this.curr = new XY();
    /** Previous cursor position */
    this.prev = new XY();

    /** Wether left button is down */
    this.left = false;
    /** Wether middle button is down */
    this.middle = false;
    /** Wether right button is down */
    this.right = false;

    /** Counter for wheel moves; +1 for wheel down; -1 for wheel up */
    this.wheels = 0;

    /**
     * Array of elements watched by MouseTracker
     * @type Array.<WatchedElement>
     * */
    this.watching = [];

    /** Unix timestamp of last move */
    this.lastMove = null;
    /** Unix timestamp of last click */
    this.lastClick = null;

    // Bind `this` to event listeners
    this.mousedown = this._mousedown.bind(this);
    this.mouseup = this._mouseup.bind(this);
    this.mousemove = this._mousemove.bind(this);
    this.wheel = this._wheel.bind(this);

    /**
     * Wether the instance has been initialized
     * @private
     * */
    this._initialized = false;
  }

  /**
   * The difference between current and previous cursor position
   * @readonly
   */
  get delta() {
    return XY.subtract(this.curr, this.prev);
  }

  /**
   * Update cursor position
   * @private
   * @param {MouseEvent} e
   */
  _updatePosition(e) {
    this.prev.x = this.curr.x;
    this.prev.y = this.curr.y;

    this.curr.x = e.clientX;
    this.curr.y = e.clientY;

    this.watching.forEach((item) => item._updatePosition(this.curr));
  }

  /**
   * Checks for pressed buttons
   * @private
   * @param {MouseEvent} e
   */
  _checkButtons(e) {
    const leftBefore = this.left;

    this.left = Boolean(e.buttons & 0x1);
    this.right = Boolean(e.buttons & 0x2);
    this.middle = Boolean(e.buttons & 0x4);

    // Look at watched elements for grab
    if (this.left) {
      if (!leftBefore) {
        this.watching.forEach((item) => (item.hover ? item._grabStart() : null));
      }
    } else {
      this.watching.forEach((item) => (item.grab ? item._grabEnd() : null));
    }

    this.lastClick = Date.now();
  }

  /**
   * Looks over watched elements for hover
   * Looks at event target and all its parent (parent of parent, etc.)
   * @private
   * @param {MouseEvent} e
   */
  _hoverTarget(e) {
    let target = e.target;
    do {
      this.watching.forEach((item) => (item.el == target ? (item.hover = true) : null));
    } while ((target = target.parentNode));
  }

  /**
   * Bound to onmousemove
   * @private
   * @param {MouseEvent} e
   */
  _mousemove(e) {
    this._updatePosition(e);

    this.watching.forEach((item) => (item.hover = false));
    this._hoverTarget(e);

    this.lastMove = Date.now();
  }

  /**
   * Bound to onmousedown event
   * @private
   * @param {MouseEvent} e
   */
  _mousedown(e) {
    this._checkButtons(e);
    this._hoverTarget(e);
  }

  /**
   * Bound to onmouseup event
   * @private
   * @param {MouseEvent} e
   */
  _mouseup(e) {
    this._checkButtons(e);
    this._hoverTarget(e);
  }

  /**
   * Bound to onwheel event
   * @private
   * @param {MouseEvent} e
   */
  _wheel(e) {
    this.wheels += e.deltaY / Math.abs(e.deltaY);
    this._hoverTarget(e);
  }

  /**
   * Returns `true` if MouseTracker is watching specified HTMLElement.
   * @param {HTMLElement} el
   */
  isWatching(el) {
    return this.watching.findIndex((watched) => watched.el == el) > -1;
  }

  /**
   * Start watching and HTMLElement if not watching already.
   * Returns WatchedElement regardless (either new one or old one).
   * @param {HTMLElement} el
   */
  watch(el) {
    const index = this.watching.findIndex((watched) => watched.el == el);

    if (index > -1) {
      return this.watching[index];
    }

    const watched = new WatchedElement(el);
    this.watching.push(watched);
    return watched;
  }

  /**
   * Stops watching an HTMLElement.
   * Returns `true` on success, `false` otherwise.
   * @param {HTMLElement} el
   */
  stopWatching(el) {
    const index = this.watching.findIndex((watched) => watched.el == el);

    if (index > -1) {
      this.watching.splice(index, 1);
      return true;
    }

    return false;
  }

  /**
   * Removes all event listeners bound to the window
   */
  kill() {
    if (this._initialized) {
      window.removeEventListener('mousemove', this.mousemove);
      window.removeEventListener('mousedown', this.mousedown);
      window.removeEventListener('mouseup', this.mouseup);
      window.removeEventListener('wheel', this.wheel);
    }
  }

  /**
   * Initiates all event listeners bound to the window
   */
  init() {
    if (!this._initialized) {
      window.addEventListener('mousemove', this.mousemove);
      window.addEventListener('mousedown', this.mousedown);
      window.addEventListener('mouseup', this.mouseup);
      window.addEventListener('wheel', this.wheel);

      this._initialized = true;
    }
  }
}

export default MouseTracker;
